import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Search, Menu, Sun, Moon, User, LogOut, LayoutDashboard, X, Bookmark,
  Heart, Newspaper, Stethoscope, AlertTriangle, Baby, Building2, Briefcase,
  BookOpen, Headphones, Megaphone, ChevronDown, Shield, Award, Flame,
  Leaf, Bug, GraduationCap, Phone, MapPin, ArrowRight
} from "lucide-react";

const NAV_STRUCTURE = [
  {
    label: "Health",
    items: [
      { label: "Health Awareness", href: "/category/health-awareness", icon: Heart, desc: "Tips and awareness for common health issues" },
      { label: "Important Health Information", href: "/category/important-health-info", icon: Shield, desc: "Critical health info everyone should know" },
      { label: "Health Warriors", href: "/category/health-warriors", icon: Flame, desc: "Stories of frontline healthcare heroes" },
      { label: "Old Traditional Methods", href: "/category/traditional-methods", icon: Leaf, desc: "Traditional healthcare practices and features" },
    ],
  },
  {
    label: "Medical News",
    items: [
      { label: "Latest Medical News", href: "/category/medical-news", icon: Newspaper, desc: "Breaking developments in healthcare" },
      { label: "Govt Scheme Ground Report", href: "/category/govt-scheme-report", icon: Building2, desc: "On-ground reports of government health schemes" },
      { label: "Hospital / Doctor Achievement", href: "/category/hospital-achievement", icon: Award, desc: "Notable achievements in healthcare" },
      { label: "Rare Disease Reporting", href: "/category/rare-disease", icon: Bug, desc: "Coverage of rare and uncommon diseases" },
    ],
  },
  {
    label: "Doctor Speak",
    items: [
      { label: "Doctor Profiles", href: "/category/doctor-speak", icon: Stethoscope, desc: "Expert opinions from verified professionals" },
      { label: "Medical Teachers Association", href: "/category/medical-teachers", icon: GraduationCap, desc: "Insights from medical educators" },
      { label: "Medical Officers Association", href: "/category/medical-officers", icon: Award, desc: "Updates from medical officer community" },
    ],
  },
  {
    label: "Hospitals",
    topLink: "/hospitals",
    items: [
      { label: "Government Hospital", href: "/category/govt-hospital", icon: Building2, desc: "Government healthcare facilities" },
      { label: "Private Hospital", href: "/category/private-hospital", icon: Building2, desc: "Private healthcare - Owners, Doctors & Staff" },
      { label: "District Hospital", href: "/category/district-hospital", icon: MapPin, desc: "District level healthcare centers" },
      { label: "Civil Hospital", href: "/category/civil-hospital", icon: Building2, desc: "Civil hospital updates and news" },
    ],
  },
  {
    label: "Careers",
    items: [
      { label: "Recruitment / Vacancies", href: "/category/recruitment", icon: Briefcase, desc: "Latest medical job openings and training" },
      { label: "Career Counselling", href: "/category/career-counselling", icon: GraduationCap, desc: "Medical career guidance and advice" },
    ],
  },
  { label: "Podcasts", href: "/category/podcasts" },
  {
    label: "Advertise",
    topLink: "/advertise",
    items: [
      { label: "Book Advertising", href: "/advertise#packages", icon: Megaphone, desc: "Advertise with Pulse Medical Media" },
      { label: "Contact Us to Share", href: "/advertise#contact", icon: Phone, desc: "Share your story or content with us" },
    ],
  },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dark, setDark] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("theme") === "dark";
    }
    return false;
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  return (
    <>
      {/* Top bar */}
      <div className="bg-slate-900 text-white py-2 text-xs font-sans tracking-wider hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <span className="uppercase tracking-[0.2em] font-medium text-slate-400">
            Trusted Medical News & Awareness Platform
          </span>
          <div className="flex items-center gap-4 text-slate-400">
            <span>{new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800" data-testid="main-header">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0" data-testid="header-logo">
              <div className="h-14 w-48 overflow-hidden flex items-center justify-center">
                <img src="https://customer-assets.emergentagent.com/job_doctor-speak-hub/artifacts/thatxx48_Pulse%20Medical%20Media%20Logo%20-%20Smooth%20Blue%20Design.svg" alt="Pulse Medical Media" className="h-[200px] w-auto" style={{marginTop: '18px'}} />
              </div>
            </Link>

            {/* Desktop Nav - Custom hover dropdowns */}
            <nav className="hidden xl:flex items-center gap-0" data-testid="desktop-nav">
              {NAV_STRUCTURE.map((item) =>
                item.items ? (
                  <NavDropdown key={item.label} item={item} />
                ) : (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="px-2.5 py-2 text-[13px] font-sans font-medium text-muted-foreground hover:text-foreground transition-colors"
                    data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-1.5 shrink-0">
              {searchOpen ? (
                <form onSubmit={handleSearch} className="flex items-center gap-2">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search articles..."
                    className="w-44 h-9 text-sm"
                    autoFocus
                    data-testid="search-input"
                  />
                  <Button type="button" variant="ghost" size="icon" onClick={() => setSearchOpen(false)} className="h-9 w-9">
                    <X className="h-4 w-4" />
                  </Button>
                </form>
              ) : (
                <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} className="h-9 w-9" data-testid="search-toggle">
                  <Search className="h-4 w-4" />
                </Button>
              )}

              <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} className="h-9 w-9" data-testid="dark-mode-toggle">
                {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>

              {user && user.role ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="user-menu-trigger">
                      <User className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem className="text-xs text-muted-foreground font-sans">{user.email}</DropdownMenuItem>
                    {user.role === "admin" && (
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-link">
                        <LayoutDashboard className="h-4 w-4 mr-2" /> Admin Panel
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate("/bookmarks")} data-testid="bookmarks-link">
                      <Bookmark className="h-4 w-4 mr-2" /> Reading List
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                      <LogOut className="h-4 w-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="hidden sm:flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => navigate("/login")} className="text-sm font-sans" data-testid="login-btn">Login</Button>
                  <Button size="sm" onClick={() => navigate("/register")} className="text-sm font-sans bg-[#1E88E5] hover:bg-[#1565C0]" data-testid="register-btn">Register</Button>
                </div>
              )}

              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="xl:hidden h-9 w-9" data-testid="mobile-menu-trigger">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80 overflow-y-auto">
                  <nav className="flex flex-col gap-1 mt-8">
                    {NAV_STRUCTURE.map((item) =>
                      item.items ? (
                        <MobileDropdown key={item.label} item={item} />
                      ) : (
                        <Link key={item.label} to={item.href} className="px-4 py-3 text-sm font-sans font-medium text-foreground hover:bg-muted transition-colors">{item.label}</Link>
                      )
                    )}
                    {!user && (
                      <div className="border-t border-slate-200 dark:border-slate-700 mt-4 pt-4">
                        <Link to="/login" className="px-4 py-3 text-sm font-sans font-medium text-[#1E88E5] block">Login</Link>
                        <Link to="/register" className="px-4 py-3 text-sm font-sans font-medium text-[#1E88E5] block">Register</Link>
                      </div>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}

/* Custom hover dropdown - positions directly under its trigger */
function NavDropdown({ item }) {
  return (
    <div className="relative group" data-testid={`nav-trigger-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
      <button className="px-2.5 py-2 text-[13px] font-sans font-medium text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
        {item.label}
        <ChevronDown className="h-3 w-3 transition-transform group-hover:rotate-180" />
      </button>

      {/* Dropdown panel */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
        <div className="w-[380px] bg-background border border-slate-200 dark:border-slate-700 shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-2">
          {item.topLink && (
            <Link
              to={item.topLink}
              className="flex items-center gap-2 p-3 mb-1 bg-[#1E88E5]/5 hover:bg-[#1E88E5]/10 border border-[#1E88E5]/20 transition-colors"
              data-testid={`nav-viewall-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <ArrowRight className="h-4 w-4 text-[#1E88E5]" />
              <span className="text-sm font-sans font-semibold text-[#1E88E5]">View All {item.label}</span>
            </Link>
          )}
          {item.items.map((sub) => (
            <Link
              key={sub.label}
              to={sub.href}
              className="flex items-start gap-3 p-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group/item"
              data-testid={`nav-sub-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="w-8 h-8 bg-[#1E88E5]/10 dark:bg-[#1E88E5]/20 flex items-center justify-center shrink-0 mt-0.5 group-hover/item:bg-[#1E88E5]/20">
                <sub.icon className="h-4 w-4 text-[#1E88E5]" />
              </div>
              <div>
                <div className="text-sm font-sans font-medium text-foreground leading-none mb-1.5">{sub.label}</div>
                <p className="text-xs font-sans text-muted-foreground leading-snug">{sub.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileDropdown({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-sans font-medium text-foreground hover:bg-muted transition-colors"
        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {item.label}
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pl-4 pb-2">
          {item.topLink && (
            <Link to={item.topLink} className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-sans text-[#1E88E5] font-medium hover:bg-muted transition-colors">
              <ArrowRight className="h-3.5 w-3.5" /> View All {item.label}
            </Link>
          )}
          {item.items.map((sub) => (
            <Link key={sub.label} to={sub.href} className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-sans text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <sub.icon className="h-3.5 w-3.5 text-[#1E88E5]" /> {sub.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
